import {
  Button,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { IconSwitchVertical, IconX } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useFormik } from "formik";

import Loading from "./Loading";
import { Input } from "./Input";

import { formatPrice } from "../helpers/format";

import { sendTokenByUserId } from "../services/user";
import { pixTransfer } from "../services/finance";
import { useState } from "react";
import OTPInput from "react-otp-input";

const ModalTransferPix = ({
  open,
  onClose,
  refetch,
  balance = 0,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  balance?: any;
}) => {
  const [value, setValue] = useState("1");
  const [otp, setOtp] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: pixTransfer,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Transferência feita com sucesso");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      onClose();
      refetch();
      toast.error(
        String(e?.response?.data?.message) || "Error ao fazer transferência"
      );
    },
  });

  const { mutate: mutateSms, isPending: isPendingSms } = useMutation({
    mutationFn: () => sendTokenByUserId({ phone: null }),
    onSuccess: () => {
      setValue("2");
      toast.success("Enviamos um WhatsApp para seu número");
    },
    onError: () => {
      toast.error("Tente novamente mais tarde.");
    },
  });

  const { values, handleSubmit, handleChange } = useFormik({
    initialValues: {
      pix: "",
      description: "",
    },
    onSubmit: (values) => {
      if (!values.pix) {
        toast.info("Digite sua chave PIX");
        return;
      }

      if (value === "1") {
        mutateSms();
        return;
      }

      if (value === "2") {
        mutate({
          pixAddressKey: values.pix,
          code: otp,
          description: values.description,
        });
        return;
      }
    },
  });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  return (
    <Dialog maxWidth="xs" open={open} onClose={() => onClose()}>
      <Loading loading={isPending || isPendingSms} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Transferência PIX
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>{" "}
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                marginBottom: 2,
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="CHAVE PIX" value="1" />
              <Tab label="OTP" value="2" disabled={value !== "2"} />
            </Tabs>
          </Grid2>
          {value === "1" && (
            <Grid2 container>
              <Grid2
                size={12}
                padding={2}
                sx={{ backgroundColor: "secondary.dark", borderRadius: 2 }}
              >
                <Stack>
                  <Typography fontSize={20} fontWeight={700}>
                    {formatPrice(balance)}
                  </Typography>
                  <Typography fontSize={14}>Saldo para resgate</Typography>
                </Stack>
              </Grid2>
              <Grid2
                container
                size={12}
                padding={2}
                sx={{ backgroundColor: "secondary.dark", borderRadius: 2 }}
              >
                <Grid2 size={12}>
                  <Stack
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={1}
                    color="primary"
                  >
                    <IconSwitchVertical style={{ color: "#7600ED" }} />
                    <Typography fontWeight={500} color="primary">
                      Favorecido
                    </Typography>
                  </Stack>
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="pix"
                    name="pix"
                    label="Chave PIX"
                    placeholder="Digite sua chave pix"
                    type="text"
                    fullWidth
                    size="small"
                    value={values.pix}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="description"
                    name="description"
                    label="Descrição"
                    placeholder="Digite a descrição"
                    type="text"
                    fullWidth
                    size="small"
                    value={values.description}
                    onChange={handleChange}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          )}
          {value === "2" && (
            <Grid2 size={12}>
              <Typography>
                Informe o token que enviamos no seu celular
              </Typography>
              <Stack justifyContent={"center"} alignItems={"center"} my={2}>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={5}
                  renderInput={(props) => <input {...props} />}
                  containerStyle={{ gap: 10 }}
                  inputStyle={{
                    height: "60px",
                    width: "50px",
                    fontSize: "32px",
                    fontWeight: 600,
                    color: "primary.contrastText",
                    backgroundColor: "secondary.light",
                    borderRadius: "10px",
                    borderStyle: "solid",
                    borderColor: "secondary.contrastext",
                  }}
                />
              </Stack>
            </Grid2>
          )}
          <Grid2 size={12}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleSubmit()}
            >
              Confirmar transferência pix
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default ModalTransferPix;
